<script>
export default {
  name: "DidoDatafileCard",
};
</script>

<script setup>
import { ref, watch, computed, inject } from "vue";
import api from "../api";
import { displayDateFormatDDMMYYYY, pause } from "../utils";
import DidoTextTruncate from "../components/commons/Truncate.vue";
import DidoChipsList from "../components/commons/chips/List.vue";

import logoEnvironnement from "../assets/theme-environnement.png";
import logoLogement from "../assets/theme-logement.png";
import logoEnergie from "../assets/theme-energie.png";
import logoTransports from "../assets/theme-transport.png";
import logoClimat from "../assets/theme-climat.png";

// inject variable to know if we are in catalog
const inCatalog = inject("inCatalog", null);

// inject method to create a catalogUrl
const catalogUrl = inject("catalogUrl", () => null);

// define Props
const props = defineProps({
  apiUrl: { type: String, required: true },
  datafileRid: { type: String, required: false, default: "" },
  datafile: { type: Object, required: false, default: null },
});

// define events emitted
const emit = defineEmits([
  "datafile-retrieved",
  "datafile-not-retrieved",
  "label-selection",
  "click-card",
  "bad-configuration",
]);

// define Reactive Data
const status = ref("");
const loading = ref(false);
const datafileObject = ref(null);
const tags = ref([]);

// define watchers
watch(
  () => [props.apiUrl, props.datafileRid, props.datafile],
  () => getDatafile(),
);

// create a computed flag to mark the widget as configured
const configured = computed(() => {
  if (!props.apiUrl) return false;
  if (!props.datafileRid && !props.datafile) return false;
  return true;
});

// computed data for the specs of QCard
const tagQCard = computed(() => (inCatalog ? "a" : "div"));
const hrefQCard = computed(() =>
  inCatalog
    ? catalogUrl.value({
        type: "datafile",
        datafileRid: datafileObject.value.rid,
      })
    : null,
);
const titleQCard = computed(() =>
  inCatalog
    ? `Voir le détail du fichier de données ${datafileObject.value.title}`
    : null,
);
const hasClickEventQCard = computed(() => (inCatalog ? "click" : null));
const clickQCard = computed(
  () => () => emit("click-card", { datafileRid: datafileObject.value.rid }),
);

// computed data for modification date
const modificationDate = computed(() => {
  const dateToDisplay =
    datafileObject.value.last_modified || datafileObject.value.created_at;
  return displayDateFormatDDMMYYYY(dateToDisplay);
});
// computed data for the topic logo
const logo = computed(() => {
  if (datafileObject.value.dataset.topic === "Environnement")
    return logoEnvironnement;
  if (datafileObject.value.dataset.topic === "Logement") return logoLogement;
  if (datafileObject.value.dataset.topic === "Énergie") return logoEnergie;
  if (datafileObject.value.dataset.topic === "Transports")
    return logoTransports;
  if (datafileObject.value.dataset.topic === "Changement climatique")
    return logoClimat;
  return "";
});
// computed data for publication date
const publicationDate = computed(() =>
  displayDateFormatDDMMYYYY(datafileObject.value.published),
);
// computed data for millesimes text
const millesimesText = computed(() => {
  if (datafileObject.value.millesimes.length > 1)
    return `Ce fichier de données contient ${datafileObject.value.millesimes.length} millésimes`;
  return "";
});
// computed data for download counter text
const downloadsCounterText = computed(() => {
  if (!datafileObject.value.downloads) return "";
  else
    return `Nombre de téléchargements : ${datafileObject.value.downloads.toLocaleString()}`;
});

// Define methods
const getDatafile = async () => {
  // if widget bad configured
  if (!configured.value) {
    status.value = "bad-configured";
    loading.value = false;
    datafileObject.value = null;
    emit("bad-configuration");
    return;
  }

  // if datafile data is available , setting it in reactive datas (no api call required !!)
  if (props.datafile) {
    datafileObject.value = props.datafile;
    status.value = "available";
  }
  // if datafile rid is set, api call for retrieving datafile and setting it in reactive datas
  if (props.datafileRid) {
    loading.value = true;
    await Promise.all([
      api.getDatafile(props.apiUrl, props.datafileRid),
      pause(500),
    ])
      .then(([apiResult]) => {
        datafileObject.value = apiResult.datafile;
        status.value = apiResult.status;
        if (status.value !== "available")
          emit("datafile-not-retrieved", {
            datafileRid: props.datafileRid,
          });
      })
      .finally(() => (loading.value = false));
    if (!datafileObject.value) return;

    emit("datafile-retrieved", { datafileRid: datafileObject.value.rid });
  }

  // get detail of tags
  tags.value = await Promise.all(
    datafileObject.value.dataset.tags.map(async (id) =>
      api
        .getTag(props.apiUrl, id)
        .then((apiTag) => ({ id, display: apiTag.data.DISPLAY }))
        .catch(() => id),
    ),
  );
};

// Init
getDatafile();
</script>

<template>
  <div class="dido-widget-datafile-card relative-position">
    <!-- Loading is finished -->
    <template v-if="!loading">
      <!-- Bad configuration of widget -->
      <div
        v-if="status === 'bad-configured'"
        class="q-pa-sm row-start-center border-secondary text-black"
      >
        <div class="col-12">Impossible de charger le composant...</div>
        <div class="col-12">Les attributs suivant sont requis:</div>
        <ul class="col-12">
          <li>api-url</li>
          <li>datafile-rid ou datafile</li>
        </ul>
      </div>
      <!-- Datafile unavailable-->
      <div
        v-if="!['available', 'bad-configured'].includes(status)"
        class="q-pa-sm column-all-center border-secondary text-black"
      >
        <div class="row-all-center">
          <QIcon
            name="mdi-numeric-4-box-multiple-outline"
            size="md"
            class="q-mr-xs"
            color="secondary"
          /><QIcon
            name="mdi-numeric-0-box-multiple-outline"
            size="md"
            class="q-mr-xs"
            color="secondary"
          /><QIcon
            name="mdi-numeric-4-box-multiple-outline"
            size="md"
            class="q-mr-xs"
            color="secondary"
          />
        </div>
        <div class="row-all-center q-mt-md">
          Impossible d'afficher le fichier de données
          <QIcon
            name="mdi-sync"
            size="xs"
            class="q-ml-lg cursor-pointer"
            color="secondary"
            title="Réessayer de charger les données"
            @click="getDatafile"
          />
        </div>
      </div>
      <!-- Datafile is here -->
      <QCard
        v-if="status == 'available'"
        :tag="tagQCard"
        :href="hrefQCard"
        :title="titleQCard"
        bordered
        square
        class="dido-main-object"
        @[hasClickEventQCard].prevent="clickQCard"
      >
        <!-- Title and Logo -->
        <div class="row-start-center no-wrap q-col-gutter-md">
          <!-- Logo -->
          <span class="col-auto self-start">
            <QAvatar square size="32px">
              <img
                :src="logo"
                :title="`Thème '${datafileObject.dataset.topic}'`"
              />
            </QAvatar>
          </span>
          <!-- Title -->
          <span class="dido-card-title">{{ datafileObject.title }}</span>
        </div>

        <!-- Dataset -->
        <div class="q-ml-xl row-start-center no-wrap">
          {{ datafileObject.dataset.title }}
        </div>

        <!-- Timestamps -->
        <div
          class="q-mt-sm row-start-center q-col-gutter-sm dido-card-timestamps"
        >
          <!-- Modification Date -->
          <span class="col-auto"> modifié le {{ modificationDate }} </span>
          <!-- Separator -->
          <span class="col-auto">|</span>
          <!-- Publication Date -->
          <span class="col-auto"> publié le {{ publicationDate }} </span>
        </div>

        <!-- Description -->
        <div class="q-mt-md row-start-center">
          <DidoTextTruncate
            :text="datafileObject.description"
            :length="200"
            class="text-justify"
            :link-classes="[
              'cursor-pointer',
              'on-right',
              'text-primary',
              'text-underline',
              'text-small',
            ]"
          />
        </div>

        <!-- Tags -->
        <div v-if="tags && tags.length" class="q-mt-md row-start-center">
          <DidoChipsList
            type="tag"
            from-dataset-or-datafile="datafile"
            :items="tags"
            size="sm"
            bg-color="secondary"
            text-color="black"
            @label-selection="($event) => emit('label-selection', $event)"
          />
        </div>

        <!-- Download counter -->
        <div
          v-if="downloadsCounterText"
          class="q-mt-md row-start-center dido-card-counter"
        >
          <span>{{ downloadsCounterText }}</span>
        </div>

        <!-- Millesimes -->
        <div
          v-if="millesimesText"
          class="q-mt-md row-start-center dido-datafile-card-millesimes"
        >
          <span class="col-12">
            {{ millesimesText }}
          </span>
        </div>
      </QCard>
    </template>

    <!-- Spinner during interaction with api -->
    <QInnerLoading :showing="loading">
      <QSpinner size="50px" color="secondary" />
      <div class="text-h6 text-secondary">Chargement...</div>
    </QInnerLoading>
  </div>
</template>

<style lang="sass" scoped>
.dido-widget-datafile-card
  min-height: 200px
</style>
